import { Skeleton } from '@sunwebgroup/uds-ui/skeleton'
import cn from 'classnames'
import { eachDayOfInterval, format, isBefore, lastDayOfMonth, parseISO } from 'date-fns'
import { isEmpty } from 'ramda'
import { use, useCallback, useEffect, useMemo } from 'react'
import * as React from 'react'
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react'
import { Day } from '@/scopes/prices/components/PriceTable/Day'
import { NavigationByWeeksContext } from '@/scopes/prices/components/PriceTable/NavigationByWeeksContext'
import { PriceCell } from '@/scopes/prices/components/PriceTable/PriceCell'
import {
  getPriceRangeSelector,
  isPriceTableLoadingStateSelector,
  pricesResponseSelector,
  selectedPriceIdSelector,
} from '@/scopes/prices/components/selectors'
import { PricesActionsContext } from '@/scopes/prices/domain/PricesActionsContext'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import styles from './GridMobile.module.css'
import * as Table from './Table'
import 'swiper/css'
import 'swiper'

export function GridMobile() {
  const { dateFrom, dateTo, minDatePrices } = usePricesSelector(getPriceRangeSelector)
  const { prices, pricesByDurationAndDateByWeeks } = usePricesSelector(pricesResponseSelector)
  const {
    focusedDate,
    navigateRight,
    navigateLeft,
    updateFocusedDate,
    setSwiper,
    swiperInstance: swiper,
    focusedSlide,
    setFocusedSlide,
    datesToDisplay,
    setDatesToDisplay,
    numberOfColumns,
    isNavigationLeftEnabled,
    isNavigationRightEnabled,
  } = React.use(NavigationByWeeksContext)

  const selectedPriceId = usePricesSelector(selectedPriceIdSelector)
  const isLoading = usePricesSelector(isPriceTableLoadingStateSelector)
  const { selectPrice } = use(PricesActionsContext)
  const selectedPrice = useMemo(() => prices?.find(price => price.id === selectedPriceId), [prices, selectedPriceId])

  const onPriceSelect = useCallback(
    (newSelectedPriceId: string) => {
      selectPrice(newSelectedPriceId)
    },
    [selectPrice],
  )

  useEffect(() => {
    swiper?.slideTo(focusedSlide, 300, false)
  }, [focusedSlide, swiper])

  useEffect(() => {
    if (!dateFrom || !dateTo || !prices || isEmpty(prices) || !focusedDate || !minDatePrices) {
      return
    }

    const minDatePricesMonth = format(focusedDate, 'yyyy-MM')
    const startDate = format(parseISO(minDatePricesMonth), 'yyyy-MM-01')
    const endDate = lastDayOfMonth(parseISO(minDatePricesMonth))

    const newDates = eachDayOfInterval({ start: parseISO(startDate), end: endDate }).map(date =>
      format(date, 'yyyy-MM-dd'),
    )

    setDatesToDisplay(newDates)
    setFocusedSlide(newDates.indexOf(format(focusedDate, 'yyyy-MM-dd')))
  }, [dateFrom, dateTo, prices, focusedDate])

  if (!dateFrom || !dateTo || !prices || isEmpty(prices) || !focusedDate) {
    return (
      <Table.Root className={styles.priceTableRoot} data-qa="price-table">
        {Array.from({ length: numberOfColumns }, (_, i) => (
          <Skeleton key={i} className={styles.columnSkeleton} />
        ))}
      </Table.Root>
    )
  }

  const today = format(new Date(), 'yyyy-MM-dd')
  const disableColumn = (date: string) => isBefore(parseISO(date), parseISO(today))

  const onSlideChangeHandler = (swiperInstance: SwiperClass) => {
    if (swiperInstance.isBeginning) {
      if (!isNavigationLeftEnabled)
        return
      navigateLeft(parseISO(datesToDisplay[0]))
    }
    else if (swiperInstance.isEnd) {
      if (!isNavigationRightEnabled)
        return
      navigateRight(parseISO(datesToDisplay[datesToDisplay.length - 1]))
    }
    else {
      updateFocusedDate(parseISO(datesToDisplay[swiperInstance.activeIndex]))
    }
  }

  return (
    <Table.Root className={styles.priceTableRoot} data-qa="price-table">
      <Swiper
        onSwiper={setSwiper}
        initialSlide={focusedSlide}
        onSlideChangeTransitionEnd={onSlideChangeHandler}
        centeredSlides
        spaceBetween={6}
        navigation
        slidesPerView={3.2}
        pagination={{ clickable: true }}
        scrollbar={{ draggable: true }}
        role="grid"
      >
        {datesToDisplay.map(date => (
          <SwiperSlide key={date}>
            {isLoading
              ? <Skeleton className={styles.columnSkeleton} />
              : (
                  <Table.Col
                    key={date}
                    className={cn(styles.priceTableColumn, disableColumn(date) && styles.priceTableColumnDisabled)}
                    role="row"
                  >
                    <Table.Header className={styles.dayHeader} role="columnheader">
                      <Day date={date} />
                    </Table.Header>
                    {!disableColumn(date)
                    && Object.keys(pricesByDurationAndDateByWeeks!).map((duration: string) => (
                      <PriceCell
                        key={duration + date}
                        price={pricesByDurationAndDateByWeeks![Number(duration)]?.[date]}
                        onClick={onPriceSelect}
                        isSelected={
                          selectedPrice?.departureDate === date && selectedPrice?.duration.toString() === duration
                        }
                      />
                    ))}
                  </Table.Col>
                )}
          </SwiperSlide>
        ))}
      </Swiper>
    </Table.Root>
  )
}
